import { isPlatformServer } from '@angular/common';
import { HttpErrorResponse, HttpInterceptorFn } from '@angular/common/http';
import { inject, PLATFORM_ID } from '@angular/core';
import { Router } from '@angular/router';
import { UserService } from '@hello-unicorn/api-client';
import { AuthTokenService, ToastService } from '@hello-unicorn/client-libs';
import { catchError, firstValueFrom, from, switchMap, throwError } from 'rxjs';

export const HttpInterceptor: HttpInterceptorFn = (req, next) => {
  const toast = inject(ToastService);
  const router = inject(Router);
  const platformId = inject(PLATFORM_ID);
  const authTokenService = inject(AuthTokenService);
  const userService = inject(UserService);

  if (isPlatformServer(platformId)) {
    return next(req);
  }

  // Clone request with Authorization header if token exists
  const accessToken = authTokenService.getAccessToken();
  let request = req;
  if (accessToken) {
    request = req.clone({
      setHeaders: {
        Authorization: `Bearer ${accessToken}`,
      },
    });
  }

  return next(request).pipe(
    catchError((error: HttpErrorResponse) => {
      // Token expired - attempt refresh
      if (error.status === 401 || error.status === 498) {
        const refreshToken = authTokenService.getRefreshToken();
        if (refreshToken) {
          return from(
            firstValueFrom(
              userService.userControllerRefresh({ body: { refreshToken } })
            )
          ).pipe(
            switchMap(res => {
              if (res.accessToken) {
                authTokenService.setAccessToken(res.accessToken);
                // Retry original request with new token
                const retryReq = req.clone({
                  setHeaders: {
                    Authorization: `Bearer ${res.accessToken}`,
                  },
                });
                return next(retryReq);
              }
              return throwError(() => error);
            }),
            catchError(() => {
              authTokenService.clearTokens();
              router.navigateByUrl('/sign-in', { replaceUrl: true });
              return throwError(() => error);
            })
          );
        }
        authTokenService.clearTokens();
        router.navigateByUrl('/sign-in', { replaceUrl: true });
        return throwError(() => error);
      }

      if (error.status === 500 || error.status === 0) {
        toast.error('서버에 오류가 발생했습니다. 잠시 후 다시 시도해주세요.');
        return throwError(() => error);
      }

      if (error.error?.message) {
        toast.error(error.error.message);
      }

      return throwError(() => error);
    })
  );
};
